import React, { useEffect, useRef } from 'react';
import './Adventure.css';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import DesignIcon from '@mui/icons-material/DesignServices';
import ShieldIcon from '@mui/icons-material/Shield';
import SettingsIcon from '@mui/icons-material/Settings';
import DevicesIcon from '@mui/icons-material/Devices';

// Register GSAP plugins
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const Adventure = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const cardRefs = useRef([]);
  const bgTextRef = useRef(null);
  const orbRefs = useRef([]);

  const cards = [
    {
      id: 1,
      icon: <DesignIcon />,
      title: 'SMART DESIGN',
      description: 'Built for rider comfort and intuitive operation..'
    },
    {
      id: 2,
      icon: <ShieldIcon />,
      title: 'RELIABLE',
      description: 'Electric drivetrain engineered for consistent performance with minimal maintenance'
    },
    {
      id: 3,
      icon: <SettingsIcon />,
      title: 'ADJUSTABLE',
      description: 'Suspension and accessories allow adaptation to different riding needs.'
    },
    {
      id: 4,
      icon: <DevicesIcon />,
      title: 'VERSATILE',
      description: 'Suitable for recreation, work sites, events, and outdoor exploration.'
    }
  ];

  useEffect(() => {
    // GSAP animations on mount and scroll
    const ctx = gsap.context(() => {
      // Background text animation
      gsap.fromTo(bgTextRef.current,
        {
          opacity: 0,
          y: 100
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 90%',
            toggleActions: 'play none none reverse'
          }
        }
      );

      // Floating orbs animation
      orbRefs.current.forEach((orb, index) => {
        if (orb) {
          gsap.to(orb, {
            y: 20,
            duration: 3 + index,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            delay: index * 0.5
          });
        }
      });

      // Heading animation
      gsap.fromTo(headingRef.current,
        {
          opacity: 0,
          y: 30
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: headingRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        }
      );

      // Cards staggered animation with enhanced effects
      gsap.fromTo(cardRefs.current,
        {
          opacity: 0,
          y: 60,
          scale: 0.85
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: 'back.out(1.4)',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 65%',
            toggleActions: 'play none none reverse'
          }
        }
      );

      // Card hover effects animation
      cardRefs.current.forEach((card, index) => {
        if (card) {
          const hoverEffect = card.querySelector('.card-hover-effect');
          if (hoverEffect) {
            gsap.to(hoverEffect, {
              rotation: 360,
              duration: 20,
              repeat: -1,
              ease: 'none'
            });
          }
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const addOrbRef = (el, index) => {
    if (el && !orbRefs.current[index]) {
      orbRefs.current[index] = el;
    }
  };

  return (
    <section className="adventure-section" ref={sectionRef}>
      {/* Background Effects */}
      <div className="background-effects">
        <div className="background-orb orb-1" ref={(el) => addOrbRef(el, 0)}></div>
        <div className="background-orb orb-2" ref={(el) => addOrbRef(el, 1)}></div>
        <div className="background-orb orb-3" ref={(el) => addOrbRef(el, 2)}></div>
      </div>

      {/* Background Text */}
      <div className="background-text" ref={bgTextRef}>
        THE FUTURE
      </div>

      <div className="adventure-container">
        {/* Main Heading */}
        <div className="heading-wrapper">
          <div className="section-label">
            <div className="label-line"></div>
            <span className="label-text">EXPERIENCE</span>
            <div className="label-line"></div>
          </div>
          <h1 className="adventure-heading" ref={headingRef}>
            YOUR ADVENTURE AWAITS
          </h1>
          <p className="sub-heading">
            Premium features designed for the modern explorer
          </p>
        </div>

        {/* Cards Grid */}
        <div className="cards-grid">
          {cards.map((card, index) => (
            <div 
              key={card.id}
              ref={el => cardRefs.current[index] = el}
              className="adventure-card"
            >
              {/* Card Hover Effect */}
              <div className="card-hover-effect"></div>
              
              {/* Card Glow Effect */}
              <div className="card-glow-effect"></div>
              
              <div className="card-content">
                <div className="card-icon-wrapper">
                  <div className="card-icon-inner">
                    {card.icon}
                  </div>
                </div>
                <h3 className="card-title">{card.title}</h3>
                <p className="card-description">{card.description}</p>
                
                {/* Card Decoration */}
                <div className="card-decoration">
                  <div className="decoration-line"></div>
                  <span className="decoration-slash">//</span>
                  <div className="decoration-line"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Decorative Line */}
        <div className="bottom-decoration">
          <div className="decorative-line"></div>
          <div className="decorative-dots">
            <div className="decorative-dot"></div>
            <div className="decorative-dot"></div>
            <div className="decorative-dot"></div>
          </div>
          <div className="decorative-line"></div>
        </div>
      </div>
    </section>
  );
};

export default Adventure;